import axios, { type AxiosError, type AxiosInstance } from "axios";

// Token getter function, will be used by Auth0Context
let tokenGetter: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (getter: () => Promise<string | null>) => {
  tokenGetter = getter;
};

// Configuration
const BASE_URL = import.meta.env.DEV
  ? "/api"
  : import.meta.env.VITE_CORE_API_URL;
const TIMEOUT = 30000;

if (!BASE_URL) {
  throw new Error("VITE_CORE_API_URL environment variable is required");
}

// Create axios instance
const coreApiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
coreApiClient.interceptors.request.use(
  async (config) => {
    // for testing some cors errors form backend
    if (config.method?.toUpperCase() === "OPTIONS") {
      console.log("OPTIONS request - skipping auth token");
      return config;
    }

    // Try to get token from the getter function ( Auth0Context)
    if (tokenGetter) {
      try {
        const token = await tokenGetter();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Failed to get token from tokenGetter:", error);
        // Fallback
        const token = localStorage.getItem("authToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } else {
      // Fallback
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (import.meta.env.DEV) {
      console.log(`🚀 Core API: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error("❌ Core API Request Error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor
coreApiClient.interceptors.response.use(
  (response) => {
    // Don't transform blob responses
    if (response.config.responseType === "blob") {
      return response;
    }

    if (import.meta.env.DEV) {
      console.log(`✅ Core API: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error: AxiosError) => {
    //HTTP errors
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.error("Core API: Unauthorized - invalid token");
          // Clear potentially invalid tokens
          localStorage.removeItem("authToken");
          // Auth0Context's tokenGetter will handle re-authentication
          break;
        case 403:
          console.error("Core API: Forbidden - insufficient permissions");
          break;
        case 404:
          console.error("Core API: Resource not found");
          break;
        case 429:
          console.error("Core API: Rate limit exceeded");
          break;
        case 500:
          console.error("Core API: Internal server error");
          break;
        default:
          console.error(`Core API: Error ${status}`, data);
      }
    } else if (error.request) {
      console.error("Core API: Network error - no response received");
    } else {
      console.error("Core API: Request setup error", error.message);
    }

    return Promise.reject(error);
  },
);

//API methods
export const coreApi = {
  // GET request
  get: async <T = any>(
    url: string,
    params?: Record<string, any>,
  ): Promise<T> => {
    const response = await coreApiClient.get<T>(url, { params });
    return response.data;
  },

  // POST request
  post: async <T = any, D = any>(
    url: string,
    data?: D,
    config?: any, // For headers, etc.
  ): Promise<T> => {
    const response = await coreApiClient.post<T>(url, data, config);
    return response.data;
  },

  // PUT request
  put: async <T = any, D = any>(url: string, data?: D): Promise<T> => {
    const response = await coreApiClient.put<T>(url, data);
    return response.data;
  },

  // PATCH request
  patch: async <T = any, D = any>(url: string, data?: D): Promise<T> => {
    const response = await coreApiClient.patch<T>(url, data);
    return response.data;
  },

  // DELETE request
  delete: async <T = any>(url: string): Promise<T> => {
    const response = await coreApiClient.delete<T>(url);
    return response.data;
  },
};

export { coreApiClient };

export default coreApi;
